import type { CrmActivityType, DealSide } from './crm-activity';

export const lessonStatus = {
  COMPLETED: 'COMPLETED',
  IN_PROGRESS: 'IN_PROGRESS',
} as const;

export const courseStatus = {
  ASSIGNED: 'ASSIGNED',
  COMPLETED: 'COMPLETED',
  IN_PROGRESS: 'IN_PROGRESS',
  NOT_ASSIGNED: 'NOT_ASSIGNED',
} as const;

export type CourseStatus = keyof typeof courseStatus;
export type LessonStatus = keyof typeof lessonStatus;

export const lessonType = {
  CRM_TRACKED_ACTIVITY: 'CRM_TRACKED_ACTIVITY',
  DOWNLOAD_FILE: 'DOWNLOAD_FILE',
  MULTI_CHOICE_QUESTIONNAIRE: 'MULTI_CHOICE_QUESTIONNAIRE',
  QUESTIONNAIRE: 'QUESTIONNAIRE',
  REQUEST_FILE: 'REQUEST_FILE',
  ROLEPLAY: 'ROLEPLAY',
  SHARE_LINK: 'SHARE_LINK',
  STRATEGY: 'STRATEGY',
  TEXT_INFO: 'TEXT_INFO',
  WATCH_VIDEO: 'WATCH_VIDEO',
} as const;

export type LessonType = keyof typeof lessonType;

export type MultiChoiceQuestionnaire = {
  minCorrectAnswers: number;
  questionList: {
    answers: {
      answer: string;
      isCorrect: boolean;
    }[];
    question: string;
  }[];
};

export const RolePlayStatus = {
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  UNCOMPLETED: 'UNCOMPLETED',
  FAILED: 'FAILED',
} as const;

export type RolePlay = {
  answer: null | string;
  answerAudioUrl: null | string;
  id: number;
  isCompleted: boolean;
  objection: string;
  objectionAudioUrl: null | string;
  pos: number;
  score: null | number;
  script: string;
  suggestion: null | string;
  status: keyof typeof RolePlayStatus;
};

type LessonBase = {
  completeButtonText: string;
  courseId: number;
  desc: string;
  id: number;
  isAllowMultiSubmit: null | boolean;
  pos: number;
  previewButtonText: null | string;
  previewUrl: null | string;
  status: LessonStatus;
  title: string;
};

// Polymorphic lesson object returned by /lms/courses and related list endpoints.
// Unused fields for a given kind are null (or empty arrays).
export type Lesson = LessonBase & {
  crmActivityCount?: null | number;
  crmActivityQuantity?: null | number;
  crmActivityType?: null | CrmActivityType;
  fileUrl: null | string;
  kind: LessonType;
  link: null | string;
  multiChoiceQuestionnaire: null | MultiChoiceQuestionnaire;
  questions: string[];
  roleplays: null | RolePlay[];
  side?: DealSide | null;
  videoUrl: null | string;
};

type CrmTrackedActivityLesson = LessonBase & {
  crmActivityCount: null | number;
  crmActivityQuantity: number;
  crmActivityType: CrmActivityType;
  fileUrl: null;
  kind: typeof lessonType.CRM_TRACKED_ACTIVITY;
  link: null;
  multiChoiceQuestionnaire: null;
  questions: [];
  roleplays: null;
  /** Omitted when the activity counts every side. */
  side: DealSide | null;
  videoUrl: null;
};

type DownloadFileLesson = LessonBase & {
  fileUrl: string;
  kind: typeof lessonType.DOWNLOAD_FILE;
  link: null;
  multiChoiceQuestionnaire: null;
  questions: [];
  roleplays: null;
  videoUrl: null;
};

type InformationLesson = LessonBase & {
  fileUrl: null;
  kind: typeof lessonType.TEXT_INFO;
  link: null;
  multiChoiceQuestionnaire: null;
  questions: [];
  roleplays: null;
  videoUrl: null;
};

type MultiChoiceQuestionnaireLesson = LessonBase & {
  email?: null | string;
  fileUrl: null;
  kind: typeof lessonType.MULTI_CHOICE_QUESTIONNAIRE;
  link: null;
  multiChoiceQuestionnaire: MultiChoiceQuestionnaire;
  questions: [];
  roleplays: null;
  videoUrl: null;
};

type QuestionnaireLesson = LessonBase & {
  email?: null | string;
  fileUrl: null;
  kind: typeof lessonType.QUESTIONNAIRE;
  link: null;
  multiChoiceQuestionnaire: null;
  questions: string[];
  roleplays: null;
  videoUrl: null;
};

type RequestFileLesson = LessonBase & {
  fileUrl: null;
  kind: typeof lessonType.REQUEST_FILE;
  link: null;
  multiChoiceQuestionnaire: null;
  questions: [];
  roleplays: null;
  videoUrl: null;
};

type RolePlayLesson = LessonBase & {
  fileUrl: null;
  kind: typeof lessonType.ROLEPLAY;
  link: null;
  multiChoiceQuestionnaire: null;
  questions: [];
  roleplays: RolePlay[];
  videoUrl: null;
};

type ShareLinkLesson = LessonBase & {
  fileUrl: null;
  kind: typeof lessonType.SHARE_LINK;
  link: string;
  multiChoiceQuestionnaire: null;
  questions: [];
  roleplays: null;
  videoUrl: null;
};

type StrategyLesson = LessonBase & {
  fileUrl: null;
  kind: typeof lessonType.STRATEGY;
  link: null;
  multiChoiceQuestionnaire: null;
  questions: [];
  roleplays: null;
  taskId: number;
  videoUrl: null;
};

type WatchVideoLesson = LessonBase & {
  fileUrl: null;
  kind: typeof lessonType.WATCH_VIDEO;
  link: null;
  multiChoiceQuestionnaire: null;
  questions: [];
  roleplays: null;
  videoUrl: string;
};

// Narrowed lesson variants for the lesson detail endpoint and task components.
export type FullLesson =
  | CrmTrackedActivityLesson
  | DownloadFileLesson
  | InformationLesson
  | MultiChoiceQuestionnaireLesson
  | QuestionnaireLesson
  | RequestFileLesson
  | RolePlayLesson
  | ShareLinkLesson
  | StrategyLesson
  | WatchVideoLesson;

export type Course = {
  categoryId: null | string;
  completedDate: null | string;
  completionVideoUrl: null | string;
  deadline: null | string;
  desc: string;
  finalized: boolean;
  id: number;
  lessons: Lesson[];
  name: string;
  pos: number;
  status: CourseStatus;
  previewUrl: null | string;
  /** When this course was assigned to the agent. */
  assignedDate: null | string;
  // Admin who manually assigned the course; not returned by the backend yet.
  assignedBy?: CourseAssigner | null;
};

export type CourseAssigner = {
  name: string;
};

// Bottleneck insight explaining why a course was recommended from CRM data.
// Not returned by the backend yet.
export type CourseInsight = {
  body: string;
  headline: string;
};

export type CourseCategory = {
  id: string;
  name: string;
};

export type CourseRecommendation = {
  lessons: Lesson[];
  desc: string;
  id: number;
  name: string;
  previewUrl?: null | string;
  status?: string;
  insight?: CourseInsight | null;
};
